import React, { useState, useMemo, useEffect } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import { Search, X, AlertTriangle, ArrowRight, Puzzle } from 'lucide-react'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import IntegrationCard from '@/frontend/components/Integrations/IntegrationCard'
import CustomMcpPanel, { type CustomMcpCredential, type CustomMcpServer } from '@/frontend/components/Integrations/CustomMcpPanel'
import ComposioAppIcon from '@/frontend/components/Integrations/ComposioAppIcon'
import CategoryFilter from '@/frontend/components/Integrations/CategoryFilter'
import GoogleAccountsModal from '@/frontend/components/DigitalWorkers/GoogleAccountsModal'
import MetaAccountsModal from '@/frontend/components/DigitalWorkers/MetaAccountsModal'
import WorkerPortalShell from '@/frontend/layouts/worker-first/WorkerPortalShell'
import OrganizationConnectionsNav from '@/frontend/layouts/worker-first/OrganizationConnectionsNav'
import { usePermissions } from '@/frontend/hooks/usePermissions'
import DashboardLayout from '@/frontend/layouts/DashboardLayout'
import type { ScopeWorker } from '@/frontend/types/inertia'
import type { GoogleAccountOption, DigitalWorkerDetail } from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import { BUILTIN_ICONS } from '@/frontend/pages/Dashboard/Integrations/constants'
import type { IntegrationItem } from '@/frontend/pages/Dashboard/Integrations/types'
import { FilterEmptyState } from '@/frontend/components/EmptyState'

import ManagedConnections, { type ManagedConnectionsProps } from '@/frontend/features/ExternalOauth/ManagedConnections'

interface Props {
  managed_oauth_connections?: ManagedConnectionsProps
  local_mcp_entry?: { title: string; path: string }
  page_title: string
  builtin_integrations: IntegrationItem[]
  composio_toolkits?: IntegrationItem[]
  categories: string[]
  scope_worker?: ScopeWorker
  // Ficha completa do agente em agent-scope: alimenta o FichaHeader dentro do
  // WorkerPortalShell (a moldura do portal). Ausente no modo org-level.
  scope_worker_header?: DigitalWorkerDetail
  // slug => per-agent enablement, present only in agent-filtered mode.
  worker_integrations?: Record<string, { integration_id?: string; enabled: boolean }>
  // Sub-account selection for the scoped agent (SZ-1562): this page hosts the
  // Google/Meta account modals that used to live on the worker Geral tab.
  google_ads_accounts?: GoogleAccountOption[]
  google_analytics_properties?: GoogleAccountOption[]
  meta_ad_accounts?: GoogleAccountOption[]
  custom_mcp_servers?: CustomMcpServer[]
  custom_mcp_credentials?: CustomMcpCredential[]
  custom_mcp_error?: string
}

const IntegrationsIndex = ({
  page_title, builtin_integrations, composio_toolkits = [], categories, scope_worker, scope_worker_header, worker_integrations = {},
  google_ads_accounts = [], google_analytics_properties = [], meta_ad_accounts = [],
  custom_mcp_servers, custom_mcp_credentials = [], custom_mcp_error,
  local_mcp_entry, managed_oauth_connections,
}: Props) => {
  const { can_manage_resources } = usePermissions()
  const [search, setSearch] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [showActiveOnly, setShowActiveOnly] = useState(false)
  // Painel do agente: dois toggles de filtro no lugar do "Somente ativas".
  const [filterAtivas, setFilterAtivas] = useState(false)
  const [filterDisponiveis, setFilterDisponiveis] = useState(false)
  const [googleModalOpen, setGoogleModalOpen] = useState(false)
  const [metaModalOpen, setMetaModalOpen] = useState(false)

  const canManageAccounts = Boolean(scope_worker) && can_manage_resources

  // OAuth returns land here with ?open_modal= so the user can pick the
  // sub-accounts right after connecting (moved from the worker Geral tab,
  // SZ-1562). The param is consumed once and stripped from the URL so
  // follow-up redirects back to this page don't reopen the modal.
  useEffect(() => {
    if (!canManageAccounts) return
    const url = new URL(window.location.href)
    const openModal = url.searchParams.get('open_modal')
    if (openModal !== 'google_accounts' && openModal !== 'meta_accounts') return

    if (openModal === 'google_accounts') setGoogleModalOpen(true)
    else setMetaModalOpen(true)
    url.searchParams.delete('open_modal')
    window.history.replaceState(window.history.state, '', url.toString())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const allIntegrations = useMemo(() => [
    ...builtin_integrations,
    ...composio_toolkits,
  ], [builtin_integrations, composio_toolkits])

  const isAgent = Boolean(scope_worker)
  const hasActiveFilters = search.trim() !== '' || selectedCategory !== null
    || showActiveOnly || filterAtivas || filterDisponiveis

  const sections = useMemo(() => {
    const q = search.trim().toLowerCase()
    const matches = (t: IntegrationItem) => !q || t.name.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q)

    const sorted = [...allIntegrations].sort((a, b) => Number(b.connected) - Number(a.connected))
    let filtered = q ? sorted.filter(matches) : sorted

    if (isAgent) {
      // Painel do agente (feedback do fundador): a página segue como o catálogo
      // (agrupado por categoria), com DOIS toggles de filtro — "Ativas neste
      // agente" e "Disponíveis na organização". Nenhum ligado = catálogo
      // completo; ligados filtram (união). "Ativa" = associada a este agente;
      // "Disponível" = conectada na org (inclui as já associadas ao agente).
      if (filterAtivas || filterDisponiveis) {
        filtered = filtered.filter((t) => {
          const wi = worker_integrations[t.slug]
          const isAtiva = wi?.enabled === true
          const isDisponivel = Boolean(wi)
          return (filterAtivas && isAtiva) || (filterDisponiveis && isDisponivel)
        })
      }
    } else if (showActiveOnly) {
      filtered = filtered.filter((t) => t.connected)
    }

    const visibleCategories = selectedCategory
      ? [selectedCategory]
      : q
        ? categories.filter((c) => c !== 'Populares')
        : categories
    return visibleCategories
      .map((category) => ({
        title: category,
        integrations: filtered.filter((t) => t.categories.includes(category)),
      }))
      .filter((s) => s.integrations.length > 0)
  }, [allIntegrations, search, selectedCategory, showActiveOnly, filterAtivas, filterDisponiveis, categories, isAgent, worker_integrations])

  const clearFilters = () => {
    setSearch('')
    setSelectedCategory(null)
    setShowActiveOnly(false)
    setFilterAtivas(false)
    setFilterDisponiveis(false)
  }

  const renderIcon = (item: IntegrationItem) =>
    item.icon_type === 'builtin'
      ? (BUILTIN_ICONS[item.slug] ?? <ComposioAppIcon logo={null} name={item.name} />)
      : <ComposioAppIcon logo={item.logo} name={item.name} />

  // Agent scope mode (SZ-1561): builtin cards with a toggleable org
  // Integration record get a per-agent switch (existing toggle_integration
  // semantics; the redirect back keeps the canonical agent URL via referer).
  const workerToggleFor = (item: IntegrationItem) => {
    const workerId = scope_worker?.id
    const toggleable = workerId && can_manage_resources ? worker_integrations[item.slug] : undefined
    if (!toggleable?.integration_id) return undefined
    return {
      enabled: toggleable.enabled,
      onToggle: (enabled: boolean) =>
        router.post(`/dashboard/digital_workers/${workerId}/toggle_integration`, {
          integration_id: toggleable.integration_id,
          enabled,
        }, { preserveScroll: true }),
    }
  }

  // Google/Meta account selection is worker-scoped; in agent mode the cards
  // get a "Selecionar contas" action that opens the corresponding modal.
  const manageAccountsFor = (item: IntegrationItem) => {
    if (!canManageAccounts || !item.connected) return undefined
    if (item.slug === 'google') {
      const count = google_ads_accounts.filter((a) => a.selected).length
        + google_analytics_properties.filter((p) => p.selected).length
      return {
        label: count > 0 ? `${count} ${count === 1 ? 'conta selecionada' : 'contas selecionadas'} · gerenciar` : 'Selecionar contas',
        onClick: () => setGoogleModalOpen(true),
      }
    }
    if (item.slug === 'meta') {
      const count = meta_ad_accounts.filter((a) => a.selected).length
      return {
        label: count > 0 ? `${count} ${count === 1 ? 'conta selecionada' : 'contas selecionadas'} · gerenciar` : 'Selecionar contas',
        onClick: () => setMetaModalOpen(true),
      }
    }
    return undefined
  }

  // Card da família do DS (radius 12, borda padrão, heading 14/650) — o
  // tracejado era resto da era Polaris.
  const notFoundCta = (
    <Link
      href="/dashboard/integrations/requests"
      className="flex items-center gap-4 rounded-xl border border-border bg-card p-5 transition-colors duration-150 hover:border-input"
    >
      <div className="shrink-0 rounded-xl bg-brand/10 p-3">
        <Puzzle className="h-6 w-6 text-brand" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-[14px] font-semibold text-foreground">Não encontrou sua integração?</h3>
        <p className="text-muted-foreground text-[13px] mt-0.5">
          Saiba como criar integrações personalizadas ou solicitar novas
        </p>
      </div>
      <ArrowRight className="h-5 w-5 text-muted-foreground shrink-0" />
    </Link>
  )

  const body = (
    <>
        {!scope_worker && <OrganizationConnectionsNav current="integrations" />}

        <div>
          {/* Título de página do Painel: 19/650 (escala do DS). */}
          <h1 className="text-[19px] font-semibold tracking-[-0.01em] text-foreground">{page_title}</h1>
          {local_mcp_entry && <Link href={local_mcp_entry.path} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">{local_mcp_entry.title}<ArrowRight className="size-3.5" /></Link>}
          <p className="text-[13px] text-muted-foreground mt-1">
            {scope_worker
              ? 'Filtre pelas ativas neste agente ou pelas disponíveis na organização para habilitar.'
              : 'Conecte suas plataformas e ferramentas.'}
          </p>
        </div>

        {!can_manage_resources && (
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Você não tem permissão para gerenciar integrações.</AlertTitle>
            <AlertDescription>Você pode visualizar as integrações conectadas, mas não pode modificá-las.</AlertDescription>
          </Alert>
        )}

        {scope_worker && managed_oauth_connections && <ManagedConnections {...managed_oauth_connections} />}

        {scope_worker && custom_mcp_servers && (
          <CustomMcpPanel
            workerId={scope_worker.id}
            servers={custom_mcp_servers}
            credentials={custom_mcp_credentials}
            error={custom_mcp_error}
            canManage={can_manage_resources}
          />
        )}

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-full sm:flex-1 sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Buscar integrações..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" aria-label="Buscar integrações" />
          </div>
          {/* Filtro de categoria só no catálogo da org — no painel do agente o
              recorte é pelos dois toggles (ativas / disponíveis). */}
          {!isAgent && <CategoryFilter categories={categories} selected={selectedCategory} onSelect={setSelectedCategory} />}
          {isAgent ? (
            // Painel do agente: dois toggles de filtro (feedback do fundador).
            <>
              <div className="flex items-center gap-2">
                <Switch id="filter-ativas" checked={filterAtivas} onCheckedChange={setFilterAtivas} />
                <Label htmlFor="filter-ativas" className="text-sm text-muted-foreground cursor-pointer">Ativas neste agente</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="filter-disponiveis" checked={filterDisponiveis} onCheckedChange={setFilterDisponiveis} />
                <Label htmlFor="filter-disponiveis" className="text-sm text-muted-foreground cursor-pointer">Disponíveis na organização</Label>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <Switch id="active-filter" checked={showActiveOnly} onCheckedChange={setShowActiveOnly} />
              <Label htmlFor="active-filter" className="text-sm text-muted-foreground cursor-pointer">Somente ativas</Label>
            </div>
          )}
          {/* No painel do agente os toggles já se limpam sozinhos — "Limpar
              filtros" fica redundante; só no catálogo da org. */}
          {!isAgent && hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
              <X className="h-3 w-3" /> Limpar filtros
            </Button>
          )}
        </div>

        {sections.map((section, index) => (
          <React.Fragment key={section.title}>
            <div className="space-y-3">
              {/* Label de seção do DS: 10.5 uppercase baixa. */}
              <h3 className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-faint-foreground">{section.title}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {section.integrations.map((item) => (
                  <IntegrationCard
                    key={item.slug}
                    name={item.name}
                    description={item.description}
                    href={item.href}
                    icon={renderIcon(item)}
                    connected={item.connected}
                    workerCount={item.worker_count}
                    accountCount={item.account_count}
                    accountLabels={item.account_labels}
                    workerToggle={workerToggleFor(item)}
                    manageAccounts={manageAccountsFor(item)}
                  />
                ))}
              </div>
            </div>
            {index === 0 && notFoundCta}
          </React.Fragment>
        ))}

        {sections.length === 0 && (
          <>
            {/* Busca/filtro sem resultado (tipo B): spot central; "limpar" só no
                catálogo da org (no agente os toggles se limpam sozinhos). O
                "peça uma integração" (notFoundCta) continua logo abaixo. */}
            <FilterEmptyState
              query={search}
              onClear={!isAgent && hasActiveFilters ? clearFilters : undefined}
              className="pb-4"
            />
            {notFoundCta}
          </>
        )}
    </>
  )

  return (
    <>
      <Head title={page_title} />
      {/* Agent-scope: SEM px-4 py-6 extra — o container canônico do portal
          (DashboardLayout: max-w-5xl mx-auto + p-4 md:p-8) já dá o espaçamento,
          igual à Show. O px-4 py-6 extra fazia a ficha/tabs SALTAR ao trocar de
          aba (SZ-1588). Org-level segue com o próprio padding. */}
      {scope_worker_header ? (
        <WorkerPortalShell worker={scope_worker_header} section="integracoes">{body}</WorkerPortalShell>
      ) : (
        <div className="px-4 py-6 space-y-6">{body}</div>
      )}

      {scope_worker && (
        <>
          <GoogleAccountsModal
            open={googleModalOpen}
            onOpenChange={setGoogleModalOpen}
            workerId={scope_worker.id}
            googleAdsAccounts={google_ads_accounts}
            googleAnalyticsProperties={google_analytics_properties}
          />
          <MetaAccountsModal
            open={metaModalOpen}
            onOpenChange={setMetaModalOpen}
            workerId={scope_worker.id}
            metaAdAccounts={meta_ad_accounts}
          />
        </>
      )}
    </>
  )
}

IntegrationsIndex.layout = (page: React.ReactElement) => <DashboardLayout>{page}</DashboardLayout>

export default IntegrationsIndex
